import { create } from "zustand";
import { devtools } from "zustand/middleware";
import {
  getWishList,
  addToWishList,
  removeItemWishList,
} from "../../services/WishList/WishListServices";
import { getProductDetail } from "../../services/Products/ProductServices";
import useConsentStore from "../Consent/useConsentStore";

// ─── Guest storage keys ──────────────────────────────────────────────────────
const GUEST_WISHLIST_KEY = "lianne_guest_wishlist";

// ─── Guest helpers ───────────────────────────────────────────────────────────
const getGuestStorage = () => useConsentStore.getState().getStorage();

const normalizeWishlistItems = (value) => {
  if (Array.isArray(value)) {
    const itemMap = new Map();
    value.forEach((item) => {
      const productId = Number(item.id || item.product_id || item);
      if (productId) {
        itemMap.set(productId, {
          ...(typeof item === "object" ? item : {}),
          id: productId,
          product_id: productId,
        });
      }
    });
    return Array.from(itemMap.values());
  }

  if (Array.isArray(value?.product_ids)) {
    return value.product_ids
      .map((id) => {
        const productId = Number(id);
        if (!productId) return null;
        const display = value.items?.[productId] || value.items?.[String(id)] || {};
        return {
          ...display,
          id: productId,
          product_id: productId,
        };
      })
      .filter(Boolean);
  }

  if (value && typeof value === "object") {
    return Object.keys(value)
      .map((key) => {
        const productId = Number(value[key]?.product_id || value[key]?.id || key);
        if (!productId) return null;
        return {
          ...value[key],
          id: productId,
          product_id: productId,
        };
      })
      .filter(Boolean);
  }

  return [];
};

const normalizeDisplayItem = (productId, productInfo = {}) => {
  const id = Number(productId || productInfo.id || productInfo.product_id);
  return {
    ...productInfo,
    id,
    product_id: id,
    name: productInfo.name || `Product ${id}`,
    slug: productInfo.slug || "",
    image: productInfo.image || productInfo.images?.[0]?.src || "",
    price: Number(productInfo.price || productInfo.sale_price || productInfo.regular_price || 0),
    regular_price: Number(productInfo.regular_price || productInfo.price || 0),
    sale_price: Number(productInfo.sale_price || 0),
    added_at: productInfo.added_at || Date.now(),
  };
};

const fetchMissingWishlistDisplay = async (items) => {
  const hydratedItems = await Promise.all(
    items.map(async (item) => {
      if (item.name && item.image) return item;

      try {
        const product = await getProductDetail(item.id || item.product_id);
        return normalizeDisplayItem(item.id || item.product_id, {
          ...item,
          name: product?.name,
          slug: product?.slug,
          image: product?.images?.[0]?.src,
          price: product?.price,
          regular_price: product?.regular_price,
          sale_price: product?.sale_price,
        });
      } catch {
        return normalizeDisplayItem(item.id || item.product_id, item);
      }
    })
  );

  return hydratedItems;
};

const buildWishlistStorageValue = (items) => {
  const normalizedItems = normalizeWishlistItems(items);
  const storageItems = {};

  normalizedItems.forEach((item) => {
    storageItems[item.id] = {
      product_id: item.product_id || item.id,
      ...(item.name && { name: item.name }),
      ...(item.slug && { slug: item.slug }),
      ...(item.image && { image: item.image }),
      ...(item.price && { price: item.price }),
      ...(item.regular_price && { regular_price: item.regular_price }),
      ...(item.sale_price && { sale_price: item.sale_price }),
      ...(item.added_at && { added_at: item.added_at }),
    };
  });

  return {
    product_ids: normalizedItems.map((item) => item.id),
    items: storageItems,
  };
};

/**
 * Đọc danh sách product IDs từ guest storage.
 * State UI dùng array; storage dùng { product_ids, items }.
 */
const readGuestWishlist = () => {
  try {
    const raw = getGuestStorage().getItem(GUEST_WISHLIST_KEY);
    return raw ? normalizeWishlistItems(JSON.parse(raw)) : [];
  } catch {
    return [];
  }
};

/**
 * Ghi danh sách guest wishlist vào storage.
 */
const writeGuestWishlist = (items) => {
  try {
    getGuestStorage().setItem(
      GUEST_WISHLIST_KEY,
      JSON.stringify(buildWishlistStorageValue(items))
    );
  } catch {}
};

const clearGuestWishlistStorage = () => {
  try {
    localStorage.removeItem(GUEST_WISHLIST_KEY);
    sessionStorage.removeItem(GUEST_WISHLIST_KEY);
  } catch {}
};

// ─── Store ───────────────────────────────────────────────────────────────────
const useWishlistStore = create(
  devtools(
    (set, get) => ({
      // ── State ──────────────────────────────────────────────────────────────
      wishlist: [],       // Array sản phẩm đầy đủ (logged-in) hoặc IDs (guest)
      isLoading: false,
      error: null,
      isUserLoggedIn: false,

      /**
       * isGuestMode: true  → dùng guest storage
       * isGuestMode: false → dùng backend API
       */
      isGuestMode: true,

      // ── Mode setter ────────────────────────────────────────────────────────
      setGuestMode: (isGuest) => set({ isGuestMode: isGuest }),

      // ── ShowWishlist ───────────────────────────────────────────────────────
      ShowWishlist: async () => {
        const { isGuestMode } = get();

        if (isGuestMode) {
          await useConsentStore.getState().waitForHydration();
          // Guest: đọc IDs từ storage, map thành minimal objects cho UI
          const stored = await fetchMissingWishlistDisplay(readGuestWishlist());
          writeGuestWishlist(stored);
          // Wishlist modal cần ít nhất { id } để render và check isInWishlist
          set({
            wishlist: stored,
            isUserLoggedIn: false,
            isLoading: false,
            error: null,
          });
          return;
        }

        // Logged-in: gọi API
        set({ isLoading: true, error: null });
        try {
          const response = await getWishList();
          const { products, is_user_logged_in } = response;
          set({
            wishlist: products || [],
            isUserLoggedIn: is_user_logged_in,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          set({
            error: error.message || "Có lỗi xảy ra khi tải wishlist",
            isLoading: false,
          });
        }
      },

      // ── addToWishlist ──────────────────────────────────────────────────────
      addToWishlist: async (productId, productInfo = {}) => {
        const { isGuestMode } = get();

        if (isGuestMode) {
          await useConsentStore.getState().waitForHydration();
          const current = readGuestWishlist();

          // Deduplicate
          const alreadyExists = current.some((item) => item.id === productId);
          if (alreadyExists) {
            return { success: true, status: "already_exists" };
          }

          const updated = [
            ...current,
            normalizeDisplayItem(productId, productInfo),
          ];
          writeGuestWishlist(updated);
          set({ wishlist: updated, error: null });
          return { success: true, status: "added" };
        }

        // Logged-in: gọi API
        set({ isLoading: true, error: null });
        try {
          const response = await addToWishList(productId);
          const { product, is_user_logged_in, status } = response;

          // Không append lại nếu backend trả already_exists
          if (status !== "already_exists") {
            const currentWishlist = get().wishlist;
            const updatedWishlist = [...currentWishlist, product];
            set({
              wishlist: updatedWishlist,
              isUserLoggedIn: is_user_logged_in,
              isLoading: false,
              error: null,
            });
          } else {
            set({ isLoading: false });
          }

          return response.data;
        } catch (error) {
          set({
            error: error.message || "Có lỗi xảy ra khi thêm vào wishlist",
            isLoading: false,
          });
          throw error;
        }
      },

      // ── removeFromWishlist ─────────────────────────────────────────────────
      removeFromWishlist: async (productId) => {
        const { isGuestMode } = get();

        if (isGuestMode) {
          await useConsentStore.getState().waitForHydration();
          const current = readGuestWishlist();
          const updated = current.filter((item) => item.id !== productId);
          writeGuestWishlist(updated);
          set({ wishlist: updated, error: null });
          return { success: true, message: "Đã xóa sản phẩm khỏi wishlist" };
        }

        // Logged-in
        set({ isLoading: true, error: null });
        try {
          await removeItemWishList(productId);
          const currentWishlist = get().wishlist;
          const updatedWishlist = currentWishlist.filter(
            (item) => item.id !== productId
          );
          set({ wishlist: updatedWishlist, isLoading: false, error: null });
          return { success: true, message: "Đã xóa sản phẩm khỏi wishlist" };
        } catch (error) {
          set({
            error: error.message || "Có lỗi xảy ra khi xóa khỏi wishlist",
            isLoading: false,
          });
          throw error;
        }
      },

      // ── isInWishlist ───────────────────────────────────────────────────────
      isInWishlist: (productId) => {
        const wishlist = get().wishlist;
        return wishlist.some((item) => item.id === productId);
      },

      // ── toggleWishlist ─────────────────────────────────────────────────────
      toggleWishlist: async (productId, productInfo = {}) => {
        const isInWishlist = get().isInWishlist(productId);
        if (isInWishlist) {
          return await get().removeFromWishlist(productId);
        } else {
          return await get().addToWishlist(productId, productInfo);
        }
      },

      // ── clearError ─────────────────────────────────────────────────────────
      clearError: () => set({ error: null }),

      // ── reset ──────────────────────────────────────────────────────────────
      reset: () =>
        set({
          wishlist: [],
          isLoading: false,
          error: null,
          isUserLoggedIn: false,
        }),

      // ── Guest-specific helpers cho merge ───────────────────────────────────
      _readGuestWishlist: readGuestWishlist,
      _clearGuestWishlistStorage: clearGuestWishlistStorage,
      _clearGuestWishlistItems: (productIds = []) => {
        const removeSet = new Set(productIds.map((id) => Number(id)));
        const updated = readGuestWishlist().filter(
          (item) => !removeSet.has(Number(item.id || item.product_id))
        );
        writeGuestWishlist(updated);
        set({ wishlist: updated, error: null });
        return updated;
      },
      _writeGuestWishlist: (items) => {
        const updated = normalizeWishlistItems(items);
        writeGuestWishlist(updated);
        set({ wishlist: updated, error: null });
      },

      // ── Getters ────────────────────────────────────────────────────────────
      getWishlistCount: () => get().wishlist.length,
      getWishlistItems: () => get().wishlist,
    }),
    { name: "wishlist-store" }
  )
);

export default useWishlistStore;
